/**
 * The two moving parts of the article template (`sections/ora-article.liquid`).
 *
 * `ora-article-toc` makes the contents links move the page and marks the
 * section being read. A plain `href="#id"` cannot do the first above 990px,
 * because `.page-wrapper` is the scroll container there and the document has
 * nothing to scroll -- the same reason `ora-jump-link` exists -- so a click
 * calls `scrollIntoView`, which finds the real scrollport and honours each
 * heading's `scroll-margin`. The href stays as the no-JS path. On a phone the
 * list lives in a `<details>`, which closes again once a link is chosen.
 *
 * `ora-article-bar` is the product bar along the foot of a phone. It stays
 * hidden until the masthead has scrolled away and hides again once the end of
 * the article is on screen, so it never sits over the newsletter or footer.
 * It is server-rendered `hidden`, so a page with no script simply never shows
 * it.
 */

class OraArticleToc extends HTMLElement {
  connectedCallback() {
    /** @type {HTMLAnchorElement[]} */
    this.links = Array.from(this.querySelectorAll('a[data-ora-toc-link]'));
    if (!this.links.length) return;

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    /** @type {Map<Element, string>} */
    this.targets = new Map();
    for (const link of this.links) {
      const id = decodeURIComponent(link.hash.slice(1));
      const target = id ? document.getElementById(id) : null;
      if (target) this.targets.set(target, id);
    }

    if (!('IntersectionObserver' in window) || !this.targets.size) return;

    /** @type {Set<Element>} */
    this.visible = new Set();
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.visible.add(entry.target);
          else this.visible.delete(entry.target);
        }
        this.markCurrent();
      },
      { rootMargin: '0px 0px -65% 0px' }
    );
    for (const target of this.targets.keys()) this.observer.observe(target);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  /**
   * The current section is the last heading that has crossed the top third of
   * the screen; when none is in that band the previous answer stands, so the
   * mark does not flicker off between two headings.
   */
  markCurrent() {
    if (!this.visible.size) return;
    let current = null;
    for (const target of this.targets.keys()) {
      if (this.visible.has(target)) current = target;
    }
    if (!current) return;
    const id = this.targets.get(current);
    for (const link of this.links) {
      const on = decodeURIComponent(link.hash.slice(1)) === id;
      link.setAttribute('aria-current', on ? 'true' : 'false');
    }
  }

  /** @param {MouseEvent} event */
  onClick(event) {
    const link = /** @type {HTMLElement} */ (event.target).closest?.('a[data-ora-toc-link]');
    if (!(link instanceof HTMLAnchorElement)) return;
    const target = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!target) return;

    event.preventDefault();
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);

    // Preventing the default also cancels the focus move fragment navigation
    // would have made, so do it by hand without a second scroll.
    if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    const details = link.closest('details');
    if (details) details.open = false;
  }
}

class OraArticleBar extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;
    const root = this.closest('.ora-art');
    const head = root?.querySelector('.ora-art__head');
    const end = root?.querySelector('[data-ora-art-end]');
    if (!head || !end) return;

    this.pastHead = false;
    this.atEnd = false;

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === head) {
          // Past the masthead only once it has left through the top.
          this.pastHead = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else if (entry.target === end) {
          this.atEnd = entry.isIntersecting || entry.boundingClientRect.top < 0;
        }
      }
      this.hidden = !(this.pastHead && !this.atEnd);
    });
    this.observer.observe(head);
    this.observer.observe(end);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('ora-article-toc')) customElements.define('ora-article-toc', OraArticleToc);
if (!customElements.get('ora-article-bar')) customElements.define('ora-article-bar', OraArticleBar);
